import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import privateAxios from "../../utils/axiosPrivate";
import TablePagesTitle from "../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../accessmanagment/page-action/AccessPageAction";
import { showToast } from "../../utils/utils";
import {
  createPricingPlan,
  commonErrMsg,
  buttonName,
  editPricingPlan,
  alertActions,
  fieldValidation,
  createAccPage
} from "../../locales/en";
import { NA, showToastType } from "../../utils/constants/appConstants";
import { useSelector } from "react-redux";
import { getPicingPlanUpdateURL } from "../../utils/constants/apiEndPoints";
import {
  isApplicableField,
  isPercentageField
} from "../../utils/constants/formDataFields";
import { useQuoteStatus } from "../../hooks/useQuoteStatus";
import { PRICING_PLANS, PRICING_PLAN_WITH_PLAN_TYPE } from "../../route/paths";
import { getPricingPlanFormConfig } from "../../utils/constants/pricingPlanFormConfig";
import PricingPlanForm from "./PricingPlanForm";

export default function EditPricingPlan() {
  const { rowId } = useParams();
  const { planGroup } = useSelector((state) => state.pricingPlan);
  const userStatus = useQuoteStatus();
  const history = useNavigate();
  const [planType, setPlanType] = useState("");

  const initialPricingPlan = {
    pricingPlanName: "",
    flatRate: "",
    annualRate: "",
    extraFlatRate: "",
    extraAnnualRate: "",
    isAutoSwitching: true,
    triggerEvents: [],
    industryCategoryId: null,
    invoiceSyncType: NA,
    eligibleOrganizationType: NA,
    applicableCountry: NA,
    ownershipStatus: NA,
    isRiskBased: false,
    minApplicableValue: "",
    maxApplicableValue: "",
    primaryPricingPlanId: ""
  };
  const [pricingPlan, setPricingPlan] = useState(initialPricingPlan);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await privateAxios.get(getPicingPlanUpdateURL(rowId));
        const data = res.data.data;

        setPlanType(data?.pricingPlanType);

        const triggerIds =
          data.triggerEvents?.[0]?.split(",").map((id) => id.trim()) || [];

        const triggerOptions = triggerIds.map((id) => {
          const match = userStatus.find((opt) => String(opt.value) === id);
          return match || { value: id, label: id };
        });

        setPricingPlan({
          ...data,
          isRiskBased: Boolean(data.isRiskBased),
          triggerEvents: triggerOptions
        });
      } catch (err) {
        showToast(
          err.response?.data?.message || commonErrMsg?.item4,
          showToastType.error
        );
      }
    };

    if (userStatus.length) loadData();
  }, [rowId, userStatus]);

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = type === "checkbox" ? checked : value;

    if (name === "isAutoSwitching") {
      newValue = value === createPricingPlan?.active;
    }

    if (isPercentageField(name)) {
      const v = parseFloat(value);
      newValue = !isNaN(v) ? Math.min(v, 100) : "";
    }

    if (isApplicableField(name)) {
      const v = parseFloat(value);
      newValue = !isNaN(v) ? Math.min(v, 1) : "";
    }

    setPricingPlan((prev) => ({
      ...prev,
      [name]: newValue
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const temp = {};

    if (!pricingPlan.pricingPlanName?.trim())
      temp.pricingPlanName = fieldValidation.required;

    if (!pricingPlan.flatRate) temp.flatRate = fieldValidation.required;
    if (!pricingPlan.annualRate) temp.annualRate = fieldValidation.required;
    if (!pricingPlan.extraFlatRate)
      temp.extraFlatRate = fieldValidation.required;
    if (!pricingPlan.extraAnnualRate)
      temp.extraAnnualRate = fieldValidation.required;

    if (!pricingPlan.triggerEvents.length)
      temp.triggerEvents = fieldValidation.required;

    if (pricingPlan.isRiskBased) {
      if (!pricingPlan.primaryPricingPlanId)
        temp.primaryPricingPlanId = fieldValidation.required;

      if (!pricingPlan.minApplicableValue)
        temp.minApplicableValue = fieldValidation.required;

      if (!pricingPlan.maxApplicableValue)
        temp.maxApplicableValue = fieldValidation.required;
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const isRiskBased = pricingPlan.isRiskBased;

      const dataToSend = {
        ...pricingPlan,
        isDefaultPlan: isRiskBased,
        planStatus: createPricingPlan?.active,
        pricingPlanType: isRiskBased
          ? createPricingPlan?.variable
          : createPricingPlan?.standard,
        primaryPricingPlanId: pricingPlan.primaryPricingPlanId
          ? Number(pricingPlan.primaryPricingPlanId)
          : null,
        ownershipStatus:
          pricingPlan.ownershipStatus.trim() !== ""
            ? pricingPlan.ownershipStatus
            : NA,
        triggerEvents: [pricingPlan.triggerEvents.map((e) => e.value).join(",")]
      };

      const res = await privateAxios.put(
        getPicingPlanUpdateURL(rowId),
        dataToSend
      );

      showToast(res.data.message, showToastType.success);

      history(
        planType === createPricingPlan?.standard
          ? PRICING_PLANS
          : PRICING_PLAN_WITH_PLAN_TYPE
      );
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        commonErrMsg?.item4;
      showToast(errorMessage, showToastType.error);
    }
  };

  const handleSelectChange = (name, val) => {
    setPricingPlan((prev) => ({
      ...prev,
      [name]: val || []
    }));
  };

  const formFields = useMemo(
    () =>
      getPricingPlanFormConfig({
        createPricingPlan,
        createAccPage,
        userStatus,
        selectedTriggers: pricingPlan.triggerEvents,
        planGroup,
        isRiskBased: pricingPlan.isRiskBased,
        isEdit: true
      }),
    [pricingPlan, userStatus, planGroup]
  );

  return (
    <>
      <TablePagesTitle
        mainTitle={createPricingPlan?.mainTitle}
        content={editPricingPlan?.content}
      />
      <div className="row mb-3 mx-0">
        <AccessPageAction subheading={editPricingPlan?.content} />
      </div>

      <div className="row mb-3 mx-0">
        <div className="col-md-12 sale-leade-form">
          <form onSubmit={handleSubmit}>
            {formFields.map((field, index) => (
              <PricingPlanForm
                key={field.name || index}
                field={field}
                formData={pricingPlan}
                errors={errors}
                onChange={onInputChange}
                handleMultiSelect={handleSelectChange}
              />
            ))}
            <div className="row mt-4">
              <div className="col-sm-8 offset-sm-4 d-flex d-sm-block justify-content-center justify-content-sm-start">
                <button
                  type="button"
                  className="btn btn-red me-2"
                  onClick={() =>
                    history(
                      planType === createPricingPlan?.standard
                        ? PRICING_PLANS
                        : PRICING_PLAN_WITH_PLAN_TYPE
                    )
                  }
                >
                  {alertActions?.Cancel}
                </button>
                <button type="submit" className="btn btn-primary">
                  {buttonName?.update}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
